import { Button } from "@bowfast/ui/components/button";
import { cn } from "@bowfast/ui/lib/utils";
import { GripVerticalIcon, PenLineIcon, Trash2Icon } from "lucide-react";
import {
  LEVEL_TIER_COLOR,
  LEVEL_TIER_LABEL,
  RATING_SYSTEM_COLOR,
  RATING_SYSTEM_LABEL,
} from "~/pages/skills/skills.config";
import type { Skill } from "~/pages/skills/skills.types";

interface SkillRowProps {
  skill: Skill;
  onEdit: (s: Skill) => void;
  onDelete: (id: string) => void;
}

export function SkillRow({ skill, onEdit, onDelete }: Readonly<SkillRowProps>) {
  return (
    <div className="group flex items-center gap-3 border-b border-border px-4 py-3 last:border-b-0 transition-colors hover:bg-muted/20">
      <GripVerticalIcon
        className="size-3.5 shrink-0 text-muted-foreground/30 opacity-0 transition-opacity group-hover:opacity-100"
        aria-hidden
      />

      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{skill.name}</p>
        {skill.description && <p className="mt-0.5 text-xs text-muted-foreground">{skill.description}</p>}
        {skill.levels.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {skill.levels.slice(0, 5).map((l) => (
              <span
                key={l.id}
                className="rounded border border-border bg-muted/40 px-1.5 py-0.5 text-[0.5625rem] font-medium text-muted-foreground"
              >
                {l.label}
                {l.numericValue != null && <span className="ml-0.5 opacity-60">·{l.numericValue}</span>}
              </span>
            ))}
            {skill.levels.length > 5 && (
              <span className="rounded border border-border bg-muted/40 px-1.5 py-0.5 text-[0.5625rem] text-muted-foreground">
                +{skill.levels.length - 5} more
              </span>
            )}
          </div>
        )}
      </div>

      <span
        className={cn(
          "hidden shrink-0 rounded-full px-2 py-0.5 text-[0.625rem] font-bold uppercase tracking-[0.04em] sm:inline-flex",
          RATING_SYSTEM_COLOR[skill.ratingSystem],
        )}
      >
        {RATING_SYSTEM_LABEL[skill.ratingSystem]}
      </span>

      {skill.level && (
        <span
          className={cn(
            "hidden shrink-0 rounded-full px-2 py-0.5 text-[0.625rem] font-bold uppercase tracking-[0.04em] md:inline-flex",
            LEVEL_TIER_COLOR[skill.level],
          )}
        >
          {LEVEL_TIER_LABEL[skill.level]}
        </span>
      )}

      <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
        <Button variant="ghost" size="icon-sm" onClick={() => onEdit(skill)}>
          <PenLineIcon className="size-3.5" />
          <span className="sr-only">Edit skill</span>
        </Button>
        <Button
          variant="ghost"
          size="icon-sm"
          className="text-coral-500 hover:text-coral-700"
          onClick={() => onDelete(skill.id)}
        >
          <Trash2Icon className="size-3.5" />
          <span className="sr-only">Delete skill</span>
        </Button>
      </div>
    </div>
  );
}
